import { useState, useMemo } from 'react';
import { classifyRI, RiStatus } from '@leopart/calc';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../hooks/useEndpoints';
import { DailyReadinessResponse } from '../types';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface HistoryEntry {
  date: string;
  ri: number;
  confidenceBadge?: string | null;
  cmj?: number | null;
  /** Unità con cui il test CMJ è stato COMPILATO (cmj resta SEMPRE in cm). Assente = cm. */
  cmjUnit?: 'cm' | 'in';
  hooper?: number | null;
  fcr?: number | null;
  hrv?: number | null;
  sleepHours?: number | null;
}

export interface BenchmarkFactor {
  mean?: number;
  stdDev?: number;
  phase?: number;
  personalBest?: number;
  lastValue?: number;
  lastDate?: string;
}

export interface ReadinessHistoryResponse {
  history: HistoryEntry[];
  benchmark: {
    cmj?: BenchmarkFactor;
    fcr?: BenchmarkFactor;
    hrv?: BenchmarkFactor;
    hooper?: BenchmarkFactor;
    [key: string]: BenchmarkFactor | undefined;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const ZONE_COLOR_MAP: Record<string, string> = {
  'Ottima': '#27ae60',
  'Buona': '#5BC9E0',
  'Media': '#FFD15A',
  'Bassa': '#f57c73',
};

export function getZone(value: number, mean: number, stdDev: number): { label: string; color: string } {
  if (value >= mean + stdDev) return { label: 'Ottima', color: '#27ae60' };
  if (value >= mean) return { label: 'Buona', color: '#5BC9E0' };
  if (value >= mean - stdDev) return { label: 'Media', color: '#FFD15A' };
  return { label: 'Bassa', color: '#f57c73' };
}

export function getZoneBadgeColor(zoneLabel: string): 'pos' | 'neg' | 'neutral' | '4' {
  if (zoneLabel === 'Ottima') return 'pos';
  if (zoneLabel === 'Buona') return '4';
  if (zoneLabel === 'Media') return 'neutral';
  return 'neg';
}

// Soglie centralizzate in @leopart/calc (classifyRI: good≥70 / normal≥50 /
// warning≥30 / critical). Prima erano 90/75/60 hardcoded, non canoniche.
const DOMAIN_COLOR: Record<RiStatus, string> = {
  good: '#27ae60',
  normal: '#FFD15A',
  warning: '#FF9F43',
  critical: '#f57c73',
};
const DOMAIN_BADGE: Record<RiStatus, string> = {
  good: '●●●',
  normal: '●●',
  warning: '●',
  critical: '○',
};

export function getDomainColor(score: number | null | undefined) {
  if (score == null) return '#b5b5b5';
  return DOMAIN_COLOR[classifyRI(score)];
}

export function getDomainBadge(score: number | null | undefined) {
  if (score == null) return '○';
  return DOMAIN_BADGE[classifyRI(score)];
}

export type ZoneType = 'Ottima' | 'Buona' | 'Media' | 'Bassa';

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseAthleteReadinessParams {
  athleteId: string;
  selectedDate: string;
}

export function useAthleteReadiness({ athleteId, selectedDate }: UseAthleteReadinessParams) {
  const endpoints = useEndpoints();

  // Pagination state for history table
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Storico range selector (giorni)
  const [historyLimit, setHistoryLimit] = useState(28);

  // ── API calls ────────────────────────────────────────────────────────────

  const dailyQuery = useQuery<DailyReadinessResponse>({
    queryKey: ['readiness-daily', athleteId, selectedDate],
    queryFn: async () => {
      const { data } = await endpoints.getReadiness(athleteId, { date: selectedDate });
      return data;
    },
    enabled: !!athleteId,
  });

  const historyQuery = useQuery<ReadinessHistoryResponse>({
    queryKey: ['readiness-history', athleteId, selectedDate, historyLimit],
    queryFn: async () => {
      const { data } = await endpoints.getReadinessHistory(athleteId, { limit: historyLimit, date: selectedDate });
      return data;
    },
    enabled: !!athleteId,
  });

  // ── Derived data ─────────────────────────────────────────────────────────

  const daily = dailyQuery.data;
  const hooper = daily?.hooper ?? null;
  const cmj = daily?.cmj ?? null;
  const fcr = daily?.fcr ?? null;
  const hrv = daily?.hrv ?? null;
  const bodyMap = daily?.bodyMap ?? null;
  const ri = daily?.ri ?? { value: 0, status: 'sotto_stress' as const, confidenceBadge: null, calibrationBadge: false, availableFactors: [], phase1Factors: [], phase2Factors: [] };

  // testPreferences is not provided by v2 API — default to CMJ
  const testPreferences = { rsiTest: 'test-cmj' as const };

  const history = useMemo(() => historyQuery.data?.history ?? [], [historyQuery.data]);
  const cmjHistoryCount = useMemo(() => history.filter(h => h.cmj != null).length, [history]);

  // Extract CMJ benchmark from nested structure, fallback to empty
  const cmjBenchmark = useMemo(() => {
    const b = historyQuery.data?.benchmark?.cmj;
    return {
      mean: b?.mean ?? 0,
      stdDev: b?.stdDev ?? 0,
      personalBest: b?.personalBest ?? 0,
      lastValue: b?.lastValue ?? 0,
      // Usa lastDate dal backend (aggiunto con commit 19700a8), non la data del primo entry history
      lastValueDate: b?.lastDate ?? '',
    };
  }, [historyQuery.data]);

  // Keep old variable name for minimal changes downstream
  const benchmark = cmjBenchmark;

  // Valore da usare per zona e indicatore:
  // - se oggi c'è un test CMJ, usa quel valore (rawValue dal daily)
  // - altrimenti usa l'ultimo storico (benchmark.lastValue)
  const cmjDisplayValue = useMemo(
    () => cmj?.rawValue ?? benchmark.lastValue,
    [cmj, benchmark.lastValue],
  );

  // Compute zone from mean ± stdDev usando cmjDisplayValue
  const computedZone = useMemo((): ZoneType => {
    if (benchmark.mean === 0) return 'Bassa';
    const v = cmjDisplayValue;
    if (v >= benchmark.mean + benchmark.stdDev) return 'Ottima';
    if (v >= benchmark.mean) return 'Buona';
    if (v >= benchmark.mean - benchmark.stdDev) return 'Media';
    return 'Bassa';
  }, [benchmark, cmjDisplayValue]);

  const benchmarkZone = { label: computedZone, color: ZONE_COLOR_MAP[computedZone] ?? '#f57c73' };

  // ── Chart data ───────────────────────────────────────────────────────────

  const chartData = useMemo(() => {
    return [...history].reverse().map(entry => ({
      date: new Date(entry.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }),
      rsi: entry.cmj != null ? Number(entry.cmj.toFixed(2)) : null,
      // Solo display: unità di compilazione per il tooltip del chart (valori restano in cm)
      cmjUnit: entry.cmjUnit,
    }));
  }, [history]);

  const yDomain = useMemo(() => {
    const values = chartData.map(d => d.rsi).filter((v): v is number => v !== null);
    if (values.length === 0) return [0, 100];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.3;
    return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
  }, [chartData]);

  const chartDataWithLevels = useMemo(() => {
    if (chartData.length === 0 || benchmark.mean === 0) return chartData;

    return chartData.map((point, index) => {
      const windowStart = Math.max(0, index - 10);
      const dataWindow = chartData.slice(windowStart, index + 1);
      const validRsi = dataWindow.map(d => d.rsi).filter((v): v is number => v !== null && v > 0);

      if (validRsi.length < 2) {
        return {
          ...point,
          excellent: Number((benchmark.mean + benchmark.stdDev).toFixed(2)),
          good: Number(benchmark.mean.toFixed(2)),
          medium: Number((benchmark.mean - benchmark.stdDev).toFixed(2)),
          low: Number((benchmark.mean - benchmark.stdDev * 2).toFixed(2))
        };
      }

      const rollingMean = validRsi.reduce((a, b) => a + b, 0) / validRsi.length;
      const rollingVariance = validRsi.reduce((sum, val) => sum + Math.pow(val - rollingMean, 2), 0) / validRsi.length;
      const rollingStdDev = Math.sqrt(rollingVariance);

      return {
        ...point,
        excellent: Number((rollingMean + rollingStdDev).toFixed(2)),
        good: Number(rollingMean.toFixed(2)),
        medium: Number((rollingMean - rollingStdDev).toFixed(2)),
        low: Number((rollingMean - rollingStdDev * 2).toFixed(2))
      };
    });
  }, [chartData, benchmark]);

  // ── RI chart data ────────────────────────────────────────────────────────

  const riChartData = useMemo(() => {
    return [...history]
      .reverse()
      .filter(entry => entry.ri != null)
      .map(entry => ({
        date: new Date(entry.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }),
        ri: entry.ri,
      }));
  }, [history]);

  // ── Sleep chart data ─────────────────────────────────────────────────────

  const sleepChartData = useMemo(() => {
    return [...history]
      .reverse()
      .filter(entry => entry.sleepHours != null)
      .map(entry => ({
        date: new Date(entry.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }),
        sleepHours: entry.sleepHours as number,
      }));
  }, [history]);

  // ── Pagination ───────────────────────────────────────────────────────────

  const totalPages = Math.ceil(history.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedHistory = history.slice(startIndex, startIndex + itemsPerPage);

  // ── Return ───────────────────────────────────────────────────────────────

  return {
    // Loading states
    isLoading: dailyQuery.isLoading || historyQuery.isLoading,
    isDailyLoading: dailyQuery.isLoading,
    isHistoryLoading: historyQuery.isLoading,
    isPending: dailyQuery.isPending && historyQuery.isPending,

    // Daily readiness
    daily,
    hooper,
    cmj,
    fcr,
    hrv,
    bodyMap,
    ri,

    // Benchmark
    benchmark,
    cmjHistoryCount,
    cmjDisplayValue,
    computedZone,
    benchmarkZone,
    testPreferences,

    // Chart
    chartData,
    chartDataWithLevels,
    yDomain,
    riChartData,
    sleepChartData,

    // History table
    history,
    paginatedHistory,
    startIndex,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage,

    // Storico range
    historyLimit,
    setHistoryLimit,
  };
}
